import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Button, TextField, MenuItem, Select, CircularProgress } from "@mui/material";
import DropOffLineChart from "./Charts/DropOffLineChart";
import DataTable from "react-data-table-component";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../../../config";

export default function FeatureDropoffDetails() {

  const navigate = useNavigate();
  const location = useLocation();
  const selectedDateRange = location.state?.selectedDateRange;
  const [dropoffData, setDropoffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDropoffData();
  }, [selectedDateRange]);

  const fetchDropoffData = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      let url = `${baseUrl}/analytics/feature-performance/dropoff`;

      // Add date filters if available
      if (selectedDateRange?.start_date && selectedDateRange?.end_date) {
        url += `?start_date=${selectedDateRange.start_date}&end_date=${selectedDateRange.end_date}`;
      }

      const response = await fetch(url, { headers: config.getHeaders() });
      const result = await response.json();
      setDropoffData(result.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching dropoff data:", err);
    } finally {
      setLoading(false);
    }
  };

  // -------- Line Chart Data --------
  const uniqueClients = [...new Set(dropoffData.map(item => item.organization_name))];
  const uniqueWeeks = [...new Set(dropoffData.map(item => item.week_start))].sort();

  const series = uniqueClients.map(clientName => {
    const clientData = dropoffData.filter(item => item.organization_name === clientName);
    return {
      name: clientName,
      data: uniqueWeeks.map(week => {
        const item = clientData.find(d => d.week_start === week);
        return item ? item.avg_gap_minutes : 0;
      })
    };
  });

  // -------- Table Data With Status Logic --------
  const classifyStatus = (avgGapMinutes) => {
    if (avgGapMinutes == null) return { label: "Unknown", style: { bg: "#E5E7EB", border: "#9CA3AF", text: "#374151" } };
    if (avgGapMinutes > 60) return { label: "Critical", style: { bg: "#FFE6E6", border: "#EF4444", text: "#B91C1C" } };
    if (avgGapMinutes > 30) return { label: "Warning", style: { bg: "#FFF4CC", border: "#F59E0B", text: "#B45309" } };
    return { label: "Stable", style: { bg: "#E8F9F0", border: "#10B981", text: "#065F46" } };
  };

  const tableRows = useMemo(() => {
    return dropoffData.map(item => {
      const status = classifyStatus(item.avg_gap_minutes);
      return { ...item, status };
    });
  }, [dropoffData]);

  // -------- Filters + Pagination --------
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredTable = useMemo(() => {
    return tableRows.filter(row =>
      row.organization_name.toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter === "All" || row.status.label === statusFilter)
    );
  }, [tableRows, search, statusFilter]);

  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const totalPages = Math.ceil(filteredTable.length / rowsPerPage);
  const displayedData = filteredTable.slice((page - 1) * rowsPerPage, page * rowsPerPage);


  // -------- Export CSV --------
  const exportCSV = () => {
    if (filteredTable.length === 0) return;
    const rows = filteredTable.map(row => ({
      Client: row.organization_name,
      "Week Start": row.week_start,
      "Avg Gap (minutes)": row.avg_gap_minutes,
      Status: row.status.label
    }));

    const csvContent = [
      Object.keys(rows[0]).join(","),
      ...rows.map(r => Object.values(r).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "FeatureDropoff_Data.csv";
    a.click();
  };


  // -------- Table Columns --------
  const columns = [
    { name: "Client", selector: row => row.organization_name, sortable: true },
    { name: "Week Start", selector: row => new Date(row.week_start).toLocaleDateString(), sortable: true },
    { name: "Avg Gap (minutes)", selector: row => row.avg_gap_minutes, sortable: true },
    {
      name: "Status",
      cell: (row) => (
        <span
          style={{
            background: row.status.style.bg,
            color: row.status.style.text,
            border: `1.5px solid ${row.status.style.border}`,
            padding: "6px 14px",
            borderRadius: "18px",
            fontWeight: 600,
            fontSize: "13px",
            textTransform: "capitalize",
          }}
        >
          {row.status.label}
        </span>
      ),
    },
  ];


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: "red" }}>
          Error loading dropoff data: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {/* BACK BUTTON */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            borderColor: "#6A5BFF",
            color: "#6A5BFF",
            "&:hover": { background: "#6A5BFF", color: "#fff" },
          }}
        >
          Back
        </Button>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Feature Drop-off — Detailed View
      </Typography>

      {/* -------- Chart Box -------- */}
      <Box sx={{ p: 3, background: "#fff", borderRadius: "10px", mb: 4, border: "1px solid #E5E7EB" }}>
        <Typography sx={{ mb: 2, fontWeight: 600 }}>Trend Overview</Typography>
        <DropOffLineChart series={series} weeks={uniqueWeeks} />
      </Box>


      {/* -------- Table Section -------- */}
      <Box sx={{ p: 3, background: "#fff", borderRadius: "10px", border: "1px solid #E5E7EB" }}>

        {/* Search + Filters + Export */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField size="small" label="Search..." value={search} onChange={e => setSearch(e.target.value)} />

            <Select size="small" sx={{ width: "140px" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Stable">Stable</MenuItem>
              <MenuItem value="Warning">Warning</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </Select>
          </Box>

          <Button variant="contained" onClick={exportCSV}>Export CSV</Button>
        </Box>

        <DataTable columns={columns} data={displayedData} highlightOnHover />

        {/* Pagination */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 2 }}>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>⬅ Prev</Button>
          <Typography>Page {page} / {totalPages}</Typography>
          <Button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next ➡</Button>
        </Box>
      </Box>
    </Box>
  );
}
